import { useState } from "react";
import { Modal, message } from "antd";
import dayjs from "dayjs";
import db from "../../../../database";
import { SyncData } from "./SyncData";
import { getCustomer } from "../../Core/customer";
import { v4 as uuidv4 } from "uuid";
import useAuthStore from "../../../../stores/useAuthStore";
import useCartStore from "../../../../stores/useCartStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { hasActiveOrdersForTable } from "../../../../utils/restaurantOrderCleanup";
import { getOpenPreparedBillsForTable, PREPARED_BILL_TABLE_ACTION_MESSAGE } from "../../../../utils/preparedBillTableLock";
import { tracePreparedBill } from "../../../../utils/preparedBillRecovery";

const useTableFeatureHandlers = ({
  tableList,
  tableCards,
  setTableCards,
  setTableList,
  setMainTableData,
  setOcupiedTablesData,
  setReservedTablesData,
  setCart,
  setSeatGuestDrawerOpen,
  setTransferBill,
  setShowProducts,
  setLoading,
  handleTable,
  setDrawerOpen,
  saveToWaitlist,
  waitlistForm,
  setWaitlistForm,
  waitlistErrors,
  setWaitlistErrors,
  validateWaitlistForm,
  setWaitlistList,
  setWaitlistTab,
  closeMergeDrawer,
  cart,
}) => {
  const tillData = useAuthStore((s) => s.tillData);

  const [selectedWaitlistGuest, setSelectedWaitlistGuest] = useState(null);
  const [selectedTableForSeating, setSelectedTableForSeating] = useState(null);
  const [isNewWaitlistCustomer, setIsNewWaitlistCustomer] = useState(false);
  const [mergeOrSplitAction, setMergeOrSplitAction] = useState("merge");
  const [selectedTablesForMerge, setSelectedTablesForMerge] = useState([]);
  const [checkInDrawerOpen, setCheckInDrawerOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [phoneError, setPhoneError] = useState("");

  const handleSeatGuestToTable = async (table, guest) => {
    table.statusType = "STD";
    table.guestName = guest.name;
    table.noOfPersons = guest.guests;
    table.mobile = guest.phone;
    table.color = "#93C5FD";
    table.time = dayjs().format("YYYY-MM-DD HH:mm:ss");
    table.tableSync = "N";
    table.startedAt = dayjs().format("YYYY-MM-DD HH:mm");

    await db.tableData.put(table);
    await SyncData(table, "upsertTableStatus");

    saveToWaitlist({
      ...guest,
      waitlistId: guest.id,
      status: "CL",
    });

    const updatedTables = await db.tableData.toArray();
    setTableCards(updatedTables);
    setMainTableData(updatedTables);
    setOcupiedTablesData(updatedTables.filter((t) => t.statusType === "OCU" || table.statusType === "ONG"));
    setReservedTablesData(updatedTables.filter((t) => t.statusType === "RES"));
    setTableList(updatedTables);

    setCart((prev) => ({
      ...prev,
      customer: {
        cwrCustomerId: guest.cwrCustomerId || null,
        name: guest.name,
        mobileNo: guest.phone,
      },
    }));

    setSeatGuestDrawerOpen(false);
  };

  const handleTransferBill = async (fromTable, toTable) => {
    if (!fromTable || !toTable) return;
    if (fromTable.cwrFbTableId === toTable.cwrFbTableId) {
      message.warning("Please select a different table for transfer");
      return;
    }

    const [fromOrders, toOrders] = await Promise.all([
      db.fbOrderData.where("cwrFbTableId").equals(fromTable.cwrFbTableId).toArray(),
      db.fbOrderData.where("cwrFbTableId").equals(toTable.cwrFbTableId).toArray(),
    ]);

    const fromOrder = fromOrders.find((o) => o.fbOrderStatus === "IP");
    const toOrder = toOrders.find((o) => o.fbOrderStatus === "IP");

    if (!fromOrder) {
      message.warning("No active bill in this table to transfer");
      return;
    }

    // BUG-2425: transferring onto an occupied table merges the two sessions into one cart
    // that matches neither table's prepared bill, so both were left un-invoiced (HMRS/10803 +
    // 10812 merged on H4). A move to a free table is fine: the bill is found by its lines.
    if (toOrder) {
      const liveCart = useCartStore.getState().cart;
      const [fromBills, toBills] = await Promise.all([getOpenPreparedBillsForTable(fromTable.cwrFbTableId, liveCart), getOpenPreparedBillsForTable(toTable.cwrFbTableId, liveCart)]);
      const blocking = [...fromBills, ...toBills];
      if (blocking.length > 0) {
        message.error(PREPARED_BILL_TABLE_ACTION_MESSAGE(blocking[0].documentno, `merged with ${toTable.name}`));
        tracePreparedBill("transfer-merge-blocked-prepared-bill", {
          fromTableId: fromTable.cwrFbTableId,
          toTableId: toTable.cwrFbTableId,
          bills: blocking.map((b) => ({ sOrderID: b.sOrderID, documentno: b.documentno })),
        });
        return;
      }
    }

    if (!toOrder) {
      const cartObj = useCartStore.getState().cart;
      // The live store cart may belong to a different table (cashier opened
      // another table since) — only trust it when it matches the source table,
      // otherwise transfer the order's own persisted cart.
      const sourceCart = cartObj?.tableId === fromTable.cwrFbTableId ? cartObj : fromOrder.cart || cartObj;
      fromOrder.cwrFbTableId = toTable.cwrFbTableId;
      fromOrder.fbOrderSync = "N";
      fromOrder.cart = {
        ...sourceCart,
        tableNo: toTable.table,
        tableId: toTable.cwrFbTableId,
      };
      await db.fbOrderData.put(fromOrder, fromOrder.fbOrderId);

      // The order has moved off the source table, but a split sibling may still
      // be live there — only flip to Available when nothing active remains.
      const fromStillActive = await hasActiveOrdersForTable(fromTable.cwrFbTableId);
      await db.tableData.update(fromTable.cwrFbTableId, {
        items: [],
        totalAmount: 0,
        statusType: fromStillActive ? "ONG" : "OPN",
        tableSync: "N",
        lines: [],
        wsData: {},
      });
      localStorage.removeItem(`isPreparedBill_${fromTable.cwrFbTableId}`);
      localStorage.removeItem(`isFoodServed_${fromTable.cwrFbTableId}`);

      // Per-key update instead of .modify() — the closure captured
      // fromOrder/fromTable, which are lost over the SQLite IPC bridge.
      const toTableRow = await db.tableData.get(toTable.cwrFbTableId);
      await db.tableData.update(toTable.cwrFbTableId, {
        items: fromOrder.cart.items || [],
        totalAmount: fromOrder.cart.total || 0,
        noOfPersons: fromTable.noOfPersons ?? toTableRow?.noOfPersons,
        guestName: fromTable.guestName ?? toTableRow?.guestName,
        waiter: fromTable.waiter ?? toTableRow?.waiter,
        statusType: "ONG",
        tableSync: "N",
        time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      });

      const [updatedFromTable] = await db.tableData
        .where("cwrFbTableId")
        .equals(fromTable.cwrFbTableId)
        .toArray();
      const [updatedToTable] = await db.tableData
        .where("cwrFbTableId")
        .equals(toTable.cwrFbTableId)
        .toArray();

      if (updatedFromTable) await SyncData(updatedFromTable, "upsertTableStatus");
      if (updatedToTable) await SyncData(updatedToTable, "upsertTableStatus");
      // skipKots: a transfer only moves the order — re-sending kots would make
      // the server re-insert and re-broadcast every historical KOT to the kitchen.
      await SyncData({ ...fromOrder, skipKots: true }, "upsertFbOrder", setLoading, "", true);

      setCart(fromOrder.cart);
      useRestaurantStore.getState().setTableName(updatedToTable);
      message.success(`Bill transferred to ${toTable.name}`);
      setShowProducts(false);
    } else {
      const fromItems = fromOrder.cart?.items || [];
      const toItems = toOrder.cart?.items || [];
      const mergedItems = [...toItems, ...fromItems];
      const mergedTotal = (toOrder.cart?.total || 0) + (fromOrder.cart?.total || 0);

      toOrder.cart = {
        ...toOrder.cart,
        items: mergedItems,
        total: mergedTotal,
        tableNo: toTable.table,
      };

      const fromLines = fromOrder.lines || [];
      const toLines = toOrder.lines || [];
      toOrder.lines = [...toLines, ...fromLines];
      toOrder.fbOrderSync = "N";

      fromOrder.cart = { ...fromOrder.cart, items: [], total: 0 };
      fromOrder.fbOrderStatus = "IP";
      fromOrder.fbOrderSync = "N";

      await db.fbOrderData.put(toOrder, toOrder.fbOrderId);
      await db.fbOrderData.put(fromOrder, fromOrder.fbOrderId);

      // fromOrder itself stays IP (emptied) on the source table, so exclude it
      // when checking whether another live order (split sibling) keeps the
      // table occupied.
      const fromRemaining = await db.fbOrderData.where("cwrFbTableId").equals(fromTable.cwrFbTableId).toArray();
      const fromStillActive = fromRemaining.some((o) => o.fbOrderStatus === "IP" && o.fbOrderId !== fromOrder.fbOrderId);
      await db.tableData.update(fromTable.cwrFbTableId, {
        items: [],
        totalAmount: 0,
        statusType: fromStillActive ? "ONG" : "OPN",
        tableSync: "N",
      });
      localStorage.removeItem(`isPreparedBill_${fromTable.cwrFbTableId}`);
      localStorage.removeItem(`isFoodServed_${fromTable.cwrFbTableId}`);

      // Per-key update instead of .modify() — the closure captured
      // mergedItems/mergedTotal, which are lost over the SQLite IPC bridge.
      await db.tableData.update(toTable.cwrFbTableId, {
        items: mergedItems,
        totalAmount: mergedTotal,
        statusType: "ONG",
        tableSync: "N",
        time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      });

      const [updatedFromTable] = await db.tableData
        .where("cwrFbTableId")
        .equals(fromTable.cwrFbTableId)
        .toArray();
      const [updatedToTable] = await db.tableData
        .where("cwrFbTableId")
        .equals(toTable.cwrFbTableId)
        .toArray();

      if (updatedFromTable) await SyncData(updatedFromTable, "upsertTableStatus");
      if (updatedToTable) await SyncData(updatedToTable, "upsertTableStatus");
      // skipKots on both: merging moves existing lines between orders — neither
      // sync represents newly fired items, so no KOTs should be (re)generated.
      await SyncData({ ...toOrder, skipKots: true }, "upsertFbOrder");
      await SyncData({ ...fromOrder, skipKots: true }, "upsertFbOrder");

      setCart(toOrder.cart);
      useRestaurantStore.getState().setTableName(updatedToTable);
      message.success(`Bill merged with ${toTable.name}`);
      setShowProducts(false);
    }

    const updatedTables = await db.tableData.toArray();
    setTableCards(updatedTables);
    setTableList(updatedTables);
    setOcupiedTablesData(updatedTables.filter((t) => t.statusType !== "OPN"));

    setSelectedTableForSeating(null);
    setSeatGuestDrawerOpen(false);
    setTransferBill(false);
  };

  const resetWaitlistForm = () => {
    setWaitlistForm({ name: "", phone: "", guests: "", eta: "" });
    setWaitlistErrors({});
  };

  const handleWaitlistContactSearch = async (e) => {
    let val = e.target.value.replace(/\D/g, "");
    e.target.value = val;

    setWaitlistForm((prev) => ({ ...prev, phone: val }));

    if (val.length > 0 && val.length !== 10) {
      setPhoneError("Contact number must be 10 digits");
      return;
    }

    setPhoneError("");

    if (val.length !== 10) {
      setWaitlistForm((prev) => ({ ...prev, name: "" }));
      setIsNewWaitlistCustomer(false);
      return;
    }

    const response = await getCustomer(null, 2, val, null, () => {}, null, tillData);
    const list = response?.data?.data?.customerSearch || [];
    const match = list.find((x) => x.mobileNo?.toString() === val.toString());

    if (match) {
      setWaitlistForm((prev) => ({ ...prev, name: match.name }));
      setIsNewWaitlistCustomer(false);
      return;
    }

    setWaitlistForm((prev) => ({ ...prev, name: "" }));
    setIsNewWaitlistCustomer(true);
  };

  const handleAddToWaitlist = () => {
    if (!validateWaitlistForm()) return;

    const newGuest = {
      id: waitlistForm.length + 1,
      name: waitlistForm.name,
      phone: waitlistForm.phone,
      guests: waitlistForm.guests,
      eta: `${waitlistForm.eta} min`,
      notified: false,
    };

    setWaitlistList((prev) => [...prev, newGuest]);
    setWaitlistForm({ name: "", phone: "", guests: "", eta: "" });
    setWaitlistErrors({});
    setWaitlistTab("current");
  };

  const handleReservationCheckIn = async (reservation, tab, statusType) => {
    const tableId = tab.cwrFbTableId;
    const sectionId = tab.cwrFbsectionId;

    if (!tableId) {
      return Modal.error({ title: "Invalid Table", content: "Please select a valid table." });
    }

    let table = await db.tableData.where("cwrFbTableId").equals(tableId).first();
    if (!table) {
      return Modal.error({
        title: "Table not found",
        content: `Table ${tableId} does not exist`,
      });
    }

    table.statusType = statusType || "RES";
    table.color = "#93C5FD";
    table.guestName = reservation.name;
    table.noOfPersons = reservation.guests;
    table.mobile = reservation.phone;
    table.time = dayjs().format("YYYY-MM-DD HH:mm:ss");
    table.tableSync = "N";

    await db.tableData.put(table);

    reservation.tableId = tableId;
    reservation.sectionId = sectionId;
    reservation.status = "CL";
    reservation.mobile = reservation.phone;

    await SyncData(table, "upsertTableStatus");
    await SyncData(reservation, "reservation");

    const updatedTables = await db.tableData.toArray();
    const sortedTables = updatedTables.sort((a, b) => a.originalIndex - b.originalIndex);

    setTableCards(sortedTables);
    setMainTableData(sortedTables);
    setOcupiedTablesData(sortedTables.filter((t) => t.statusType === "OCU"));
    setReservedTablesData(sortedTables.filter((t) => t.statusType === "RES"));
    setTableList(sortedTables);

    setCart((prev) => ({
      ...prev,
      customer: {
        cwrCustomerId: reservation.cwrCustomerId || null,
        name: reservation.name,
        mobileNo: reservation.phone,
      },
    }));
    localStorage.setItem(
      "cartObj",
      JSON.stringify({
        ...cart,
        customer: {
          cwrCustomerId: reservation.cwrCustomerId || null,
          name: reservation.name,
          mobileNo: reservation.phone,
        },
      })
    );
    handleTable(table);
    setDrawerOpen(false);
  };

  const getSplitSubTables = () => {
    const splitRows = [];
    tableList
      .filter((tbl) => tbl.isSplitParent === "Y" && tbl.isactive === "Y")
      .forEach((tbl) => {
        (tbl.splitTables || []).forEach((st) => {
          if (st.status !== "OPN") return; // exclude occupied splits
          splitRows.push({
            cwrFbTableId: `${tbl.cwrFbTableId}_${st.splitTable}`,
            parentCwrFbTableId: tbl.cwrFbTableId,
            name: st.splitTable,
            capacity: st.guests,
            sectionName: tbl.sectionName,
            statusType: st.status,
            fbtableStatusId: null,
            splitTable: st.splitTable,
            isSplitRow: true,
          });
        });
      });
    return splitRows;
  };

  const handleTableSelection = (cwrFbTableId) => {
    const allAvailable = [
      ...tableList.filter(isNormalAvailableTable),
      ...getSplitSubTables(),
    ];

    const clickedEntry = allAvailable.find((t) => t.cwrFbTableId === cwrFbTableId);

    setSelectedTablesForMerge((prev) => {
      // Deselect if already selected
      if (prev.includes(cwrFbTableId)) {
        return prev.filter((id) => id !== cwrFbTableId);
      }

      // For split action, single selection only
      if (mergeOrSplitAction === "split") {
        return [cwrFbTableId];
      }

      // If selecting a split row, check existing selections
      if (clickedEntry?.isSplitRow && prev.length > 0) {
        const firstEntry = allAvailable.find((t) => t.cwrFbTableId === prev[0]);

        // If first selected is also a split row from a DIFFERENT parent - block
        if (
          firstEntry?.isSplitRow &&
          firstEntry?.parentCwrFbTableId !== clickedEntry?.parentCwrFbTableId
        ) {
          message.error("You can only merge split tables from the same parent table.");
          return prev;
        }

        // If first selected is a normal table - block mixing with split
        if (!firstEntry?.isSplitRow) {
          message.error("You cannot merge a split table with a regular table.");
          return prev;
        }
      }

      // If selecting a normal table but split rows already selected - block
      if (!clickedEntry?.isSplitRow && prev.length > 0) {
        const firstEntry = allAvailable.find((t) => t.cwrFbTableId === prev[0]);
        if (firstEntry?.isSplitRow) {
          message.error("You cannot merge a regular table with a split table.");
          return prev;
        }
      }

      return [...prev, cwrFbTableId];
    });
  };

  const handleActionChange = (action) => {
    setMergeOrSplitAction(action);
    setSelectedTablesForMerge([]);
  };

  const handleSplitTableClick = async () => {
    if (mergeOrSplitAction !== "split" || selectedTablesForMerge.length !== 1) {
      message.warning("Please select exactly one table to split");
      return;
    }

    const tableId = selectedTablesForMerge[0];
    const table = tableList.find((t) => t.cwrFbTableId === tableId);

    if (!table) {
      message.error("Table not found");
      return;
    }

    const capacity = table.capacity || 0;
    if (capacity <= 1) {
      message.error("Cannot split table with capacity 1 or less");
      return;
    }

    const splitA = Math.ceil(capacity / 2);
    const splitB = Math.floor(capacity / 2);

    const splitConfigs = [
      {
        splitTable: `${table.name}-1`,
        status: "OPN",
        guests: splitA,
        guestName: "",
        guestType: "",
        salesRepId: table.salesRepId || "",
      },
      {
        splitTable: `${table.name}-2`,
        status: "OPN",
        guests: splitB,
        guestName: "",
        guestType: "",
        salesRepId: table.salesRepId || "",
      },
    ];

    await db.tableData.update(table.cwrFbTableId, {
      isSplitParent: "Y",
      splitTables: splitConfigs,
      displayName: `${table.name} (Split)`,
      color: "#f4a261",
      tabbleSync: "N",
    });

    const parentTableData = await db.tableData.get(table.cwrFbTableId);
    await SyncData(parentTableData, "upsertTableStatus");

    const updatedTables = await db.tableData.toArray();

    setTableCards(updatedTables);
    setTableList(updatedTables);
    if (setMainTableData) setMainTableData(updatedTables);

    message.success(
      `Table split successfully into ${table.name}-1 (${splitA} capacity) and ${table.name}-2 (${splitB} capacity)`
    );
    closeMergeDrawer();
    setSelectedTablesForMerge([]);
  };

  const handleMergeTables = async () => {
    if (mergeOrSplitAction !== "merge" || selectedTablesForMerge.length < 2) return;

    const allAvailable = [
      ...tableList.filter(isNormalAvailableTable),
      ...getSplitSubTables(),
    ];

    const masterKey = selectedTablesForMerge[0];
    const childKeys = selectedTablesForMerge.slice(1);

    const masterEntry = allAvailable.find((t) => t.cwrFbTableId === masterKey);
    if (!masterEntry) return;

    const masterTableId = masterEntry.isSplitRow
      ? masterEntry.parentCwrFbTableId
      : masterEntry.cwrFbTableId;

    const masterTable = tableList.find((t) => t.cwrFbTableId === masterTableId);
    if (!masterTable) return;

    const allAreSplitRows = selectedTablesForMerge.every((key) => {
      const entry = allAvailable.find((t) => t.cwrFbTableId === key);
      return entry?.isSplitRow;
    });

    const allSameParent =
      allAreSplitRows &&
      selectedTablesForMerge.every((key) => {
        const entry = allAvailable.find((t) => t.cwrFbTableId === key);
        return entry?.parentCwrFbTableId === masterEntry.parentCwrFbTableId;
      });

    let item;

    if (allSameParent) {
      // CASE 1: Merging split sub-tables back into parent (e.g. T3-1 + T3-2 -> T3)
      item = {
        fbtableStatusId: null,
        cwrFbTableId: masterTable.cwrFbTableId,
        cwrFbsectionId: masterTable.cwrFbSectionId || masterTable.cwrFbsectionId,
        statusType: "OPN",
        noOfPersons: masterTable.noOfPersons || 0,
        guestName: masterTable.guestName || "",
        guestType: masterTable.guestType || "",
        referredBy: masterTable.referredBy || "",
        waiter: masterTable.waiter || null,
        splitTables: [],
        merge: [],
      };
    } else {
      // CASE 2: Merging different tables (e.g. T4 + T1)
      const mergeEntries = childKeys.map((key) => {
        const entry = allAvailable.find((t) => t.cwrFbTableId === key);
        return {
          fbMergeId: entry?.fbtableStatusId,
          fbMTableId: entry?.isSplitRow ? entry.parentCwrFbTableId : key,
          fbMergeReason: "",
        };
      });

      const masterFbtableStatusId = masterEntry.isSplitRow
        ? masterEntry.fbtableStatusId
        : masterTable.fbtableStatusId;

      item = {
        fbtableStatusId: masterFbtableStatusId,
        cwrFbTableId: masterTable.cwrFbTableId,
        cwrFbsectionId: masterTable.cwrFbSectionId || masterTable.cwrFbsectionId,
        statusType: "ONG",
        noOfPersons: masterTable.noOfPersons || 0,
        guestName: masterTable.guestName || "",
        guestType: masterTable.guestType || "",
        referredBy: masterTable.referredBy || "",
        waiter: masterTable.waiter || null,
        merge: mergeEntries,
        splitTables: [],
      };
    }

    // Call API and gate ALL DB writes on success
    let syncSuccess = false;
    try {
      const result = await SyncData(item, "upsertTableStatus");
      if (result?.success) {
        syncSuccess = true;
      } else {
        console.warn("Sync failed:", result);
      }
    } catch (err) {
      console.error("SyncData failed:", err);
    }

    if (!syncSuccess) {
      // API failed - do not touch local DB, just close
      closeMergeDrawer();
      setSelectedTablesForMerge([]);
      setMergeOrSplitAction("merge");
      return;
    }

    const involvedParentIds = new Set();
    selectedTablesForMerge.forEach((key) => {
      const entry = allAvailable.find((t) => t.cwrFbTableId === key);
      if (entry?.isSplitRow) {
        involvedParentIds.add(entry.parentCwrFbTableId);
      }
    });
    // Always include master table
    involvedParentIds.add(masterTable.cwrFbTableId);

    for (const parentId of involvedParentIds) {
      const parentTable = tableList.find((t) => t.cwrFbTableId === parentId);

      const existing = await db.tableData
        .where("cwrFbTableId")
        .equals(parentId)
        .first();

      if (existing) {
        const updated = {
          ...existing,
          splitTables: [],
          isSplitParent: "N",
          displayName: parentTable?.name || existing.name || "",
          statusType: allSameParent ? "OPN" : "ONG",
          isMergedParent: allSameParent ? "N" : "Y",
          merge: allSameParent ? [] : item.merge,
          tableSync: "N",
        };
        await db.tableData.put(updated);
      }
    }

    // For non-split child normal tables, mark as merged child
    if (!allSameParent) {
      for (const key of childKeys) {
        const entry = allAvailable.find((t) => t.cwrFbTableId === key);
        if (!entry?.isSplitRow) {
          await db.tableData
            .where("cwrFbTableId")
            .equals(key)
            .modify((t) => {
              t.isMergedChild = "Y";
              t.parentTableId = masterTable.cwrFbTableId;
              t.merge = null;
              t.tableSync = "N";
            });
        }
      }

      // Update master table
      await db.tableData
        .where("cwrFbTableId")
        .equals(masterTable.cwrFbTableId)
        .modify((t) => {
          t.statusType = "ONG";
          t.isMergedParent = "Y";
          t.mergedTableIds = childKeys;
          t.merge = item.merge;
          t.tableSync = "N";
          const childNames = childKeys
            .map((key) => {
              const entry = allAvailable.find((e) => e.cwrFbTableId === key);
              return entry?.name;
            })
            .filter(Boolean);
          t.displayName = [masterEntry.name, ...childNames].join(" + ");
        });
    }

    const updatedTables = await db.tableData.toArray();
    setTableCards(updatedTables);
    setTableList(updatedTables);

    closeMergeDrawer();
    setSelectedTablesForMerge([]);
    setMergeOrSplitAction("merge");
  };

  const isNormalAvailableTable = (tbl) => {
    return (
      tbl.statusType === "OPN" &&
      tbl.isactive === "Y" &&
      tbl.isMergedParent !== "Y" &&
      tbl.isMergedChild !== "Y" &&
      tbl.isSplitParent !== "Y" &&
      tbl.ordered !== "Y"
    );
  };

  return {
    // state
    selectedWaitlistGuest,
    setSelectedWaitlistGuest,
    selectedTableForSeating,
    setSelectedTableForSeating,
    isNewWaitlistCustomer,
    mergeOrSplitAction,
    setMergeOrSplitAction,
    selectedTablesForMerge,
    setSelectedTablesForMerge,
    checkInDrawerOpen,
    setCheckInDrawerOpen,
    selectedReservation,
    setSelectedReservation,
    phoneError,
    // handlers
    handleSeatGuestToTable,
    handleTransferBill,
    resetWaitlistForm,
    handleWaitlistContactSearch,
    handleAddToWaitlist,
    handleReservationCheckIn,
    handleTableSelection,
    handleActionChange,
    handleSplitTableClick,
    handleMergeTables,
    isNormalAvailableTable,
    getSplitSubTables,
  };
};

export default useTableFeatureHandlers;
